import axios from 'axios';

export enum StudentStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PASSED_OUT = 'PASSED_OUT',
  TC = 'TC',
  RE_STUDY = 'RE_STUDY',
}

export interface StudentParent {
  id?: string;
  fatherName?: string;
  fatherMobile?: string;
  fatherOccupationId?: string;
  motherName?: string;
  motherMobile?: string;
  motherOccupationId?: string;
  guardianName?: string;
  guardianMobile?: string;
  guardianRelationId?: string;
}

export interface StudentDocument {
  id?: string;
  name: string;
  url: string;
  type: string;
}

export interface StudentAcademic {
  id?: string;
  academicSessionId: string;
  classId: string;
  sectionId: string;
  rollNumber?: string;
  isPromoted?: boolean;
}

export interface StudentSessionRecord {
  id: string;
  academicSessionId: string;
  classId: string;
  sectionId: string;
  dateOfAdmission?: string;
  academicSession?: { id: string; name: string };
  class?: { id: string; name: string };
  section?: { id: string; name: string };
}

export interface Student {
  id: string;
  admissionNumber: string;
  scholarNumber?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
  address?: string;
  bloodGroup?: string;
  bloodGroupId?: string;
  nationalityId?: string;
  motherTongueId?: string;
  studentTypeId?: string;
  aadharNumber?: string;
  status: StudentStatus;
  admissionDate?: string;
  
  // Relations
  classId: string;
  sectionId: string;
  casteId?: string;
  categoryId?: string;
  religionId?: string;
  houseId?: string;
  
  class?: { id: string; name: string };
  section?: { id: string; name: string };
  
  parent?: StudentParent;
  documents?: StudentDocument[];
  academicRecords?: StudentAcademic[];
  sessionRecords?: StudentSessionRecord[];
  statusHistory?: {
    id: string;
    oldStatus: StudentStatus;
    newStatus: StudentStatus;
    reason?: string | null;
    effectiveDate?: string | null;
    changedBy?: string | null;
    createdAt?: string;
  }[];
}

export interface CreateStudentDto {
  admissionNumber: string;
  scholarNumber: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
  address?: string;
  bloodGroup?: string;
  bloodGroupId?: string;
  nationalityId?: string;
  motherTongueId?: string;
  studentTypeId?: string;
  aadharNumber?: string;
  status?: StudentStatus;
  
  classId: string;
  sectionId: string;
  academicSessionId: string;
  admissionDate?: string;
  
  casteId?: string;
  categoryId?: string;
  religionId?: string;
  houseId?: string;
  
  parent?: StudentParent;
  documents?: StudentDocument[];
}

export interface UpdateStudentDto extends Partial<CreateStudentDto> {}

export const studentsApi = {
  getNextAdmissionNumber: async () => {
    const response = await axios.get('/scholar-register/students/next-admission-number');
    return response.data as { admissionNumber: string };
  },

  getAll: async (params?: { 
    page?: number; 
    limit?: number; 
    academicSessionId?: string;
    classId?: string; 
    sectionId?: string; 
    status?: string;
    search?: string;
    rte?: boolean;
    bpl?: boolean;
  }) => {
    const response = await axios.get('/scholar-register/students', { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await axios.get(`/scholar-register/students/${id}`);
    return response.data;
  },

  create: async (data: CreateStudentDto) => {
    const response = await axios.post('/scholar-register/students', data);
    return response.data;
  },

  downloadBulkTemplate: async () => {
    const response = await axios.get('/scholar-register/students/bulk-template', {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  bulkUpload: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post('/scholar-register/students/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  update: async (id: string, data: UpdateStudentDto) => {
    const response = await axios.patch(`/scholar-register/students/${id}`, data);
    return response.data;
  },

  changeStatus: async (id: string, status: StudentStatus) => {
    const response = await axios.patch(`/scholar-register/students/${id}/status`, { status });
    return response.data;
  },

  delete: async (id: string) => {
    const response = await axios.delete(`/scholar-register/students/${id}`);
    return response.data;
  },

  uploadFile: async (file: File, folder?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post('/media/upload', formData, {
      params: folder ? { folder } : undefined,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};
